import { doc, getDoc, getFirestore, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref } from "firebase/storage";
import { v4 as uuidv4 } from 'uuid';
import { processAndUploadFile, DICOM_FIELDS } from '../shared/utils/dicomUtils';
import { patientImagesUploaded } from './patientService';
import { encryptDisplayName } from '../shared/utils/encryptionUtils';

const usersDbName = `${import.meta.env.VITE_USERS_DB_PREFIX}-${import.meta.env.VITE_LOCATION_SHORTHAND}`
const operationsDbName = `${import.meta.env.VITE_OPERATIONS_DB_PREFIX}-${import.meta.env.VITE_LOCATION_SHORTHAND}`

export const UPLOAD_PENDING_STORAGE_KEY = 'replico_upload_pending';

const UPLOAD_PROGRESS_CAP = 95;
const FINALIZING_PROGRESS = 99;
const COMPLETE_PROGRESS = 100;
const DEMO_FINALIZATION_DELAY_MS = 5000;
const API_TIMEOUT_MS = 30000;
const MAX_CONCURRENT_UPLOADS = 10;

/**
 * @typedef {{ patientId: string, tasks: Set<any>, cancelled: boolean, finalizing: boolean }} ActiveUploadSession
 */
/** Tracks the in-flight upload session (if any) per uid, so it can be cancelled from outside `startUpload`. @type {Map<string, ActiveUploadSession>} */
const activeUploadSessions = new Map();

// ---------------------------------------------------------------------------
// Misc utilities
// ---------------------------------------------------------------------------

/** Returns a Promise that resolves after `ms` milliseconds. */
/** @param {number} ms */
export function timeout(ms) {
    return new Promise(res => setTimeout(res, ms));
}

/**
 * @param {{
 *   uploadedBytesByPath: Map<string, number>,
 *   totalBytes: number,
 *   uploaded: number,
 *   total: number,
 * }} params
 */
export function getAggregatedUploadProgress({ uploadedBytesByPath, totalBytes, uploaded, total }) {
    const uploadedBytes = Array.from(uploadedBytesByPath.values()).reduce((sum, value) => sum + value, 0);
    const uploadFraction = totalBytes > 0 ? uploadedBytes / totalBytes : (total > 0 ? uploaded / total : 0);
    return Math.min(UPLOAD_PROGRESS_CAP, Math.round(uploadFraction * UPLOAD_PROGRESS_CAP));
}

/**
 * Wraps a fetch call with a timeout.
 * @param {string} url
 * @param {RequestInit} options
 * @param {number} timeoutMs
 * @returns {Promise<Response>}
 */
async function fetchWithTimeout(url, options, timeoutMs) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
        const response = await fetch(url, {
            ...options,
            signal: controller.signal,
        });
        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        throw error;
    }
}

// ---------------------------------------------------------------------------
// Firestore helpers
// ---------------------------------------------------------------------------

/**
 * Fetches the current user's Firestore document and resolves with its data.
 * @param {string} uid
 * @returns {Promise<any>}
 */
export async function fetchUserInfo(uid) {
    try {
        const userRef = doc(getFirestore(usersDbName), "users", uid);
        const snapshot = await getDoc(userRef);
        
        if (!snapshot.exists()) {
            throw new Error(`User document not found for uid: ${uid}`);
        }
        
        return snapshot.data();
    } catch (error) {
        console.error('Error fetching user info:', error);
        throw new Error(`Failed to fetch user information: ${error.message}`);
    }
}

/**
 * Creates the patient document in the operations-progress database.
 * @param {string} patientId
 * @param {string} userId
 * @param {string} displayName
 * @param {string} [hospitalId]
 * @param {string} sex
 * @param {boolean} tumor
 * @param {string | null} [examDate] ISO "YYYY-MM-DD" calendar-date string, or null/undefined if not captured
 */
export async function createPatientDoc(patientId, userId, displayName, hospitalId, sex, tumor, examDate) {
    try {
        const patientRef = doc(
            getFirestore(operationsDbName),
            "patients",
            patientId
        );
        await setDoc(patientRef, {
            user_id: userId,
            hospital_id: hospitalId || null,
            nerves_done: false,
            patient_id: patientId,
            diffusion_done: false,
            seg_done: false,
            seg_validated: false,
            model_exported: false,
            step: "upload",
            display_name: await encryptDisplayName(displayName),
            sex: sex,
            tumor: tumor,
            exam_date: examDate ?? null,
            createdTime: serverTimestamp(),
        });
    } catch (error) {
        console.error('Error creating patient document:', error);
        throw new Error(`Failed to create patient document: ${error.message}`);
    }
}

/**
 * Marks the user document as "uploading" and stores the active patientId.
 * @param {string} uid
 * @param {string} patientId
 */
export async function markUserUploading(uid, patientId) {
    try {
        const userRef = doc(getFirestore(usersDbName), "users", uid);
        await updateDoc(userRef, { patientId, uploadStatus: "uploading" });
    } catch (error) {
        console.error('Error marking user as uploading:', error);
        throw new Error(`Failed to update user upload status: ${error.message}`);
    }
}

/**
 * Checks whether the user has a stale upload session (e.g. from a page refresh
 * mid-upload) and marks the associated patient document as failed if so.
 * Best-effort — never throws.
 * @param {string} uid
 */
export async function checkAndCleanupStaleUpload(uid) {
    try {
        const userRef = doc(getFirestore(usersDbName), 'users', uid);
        const snapshot = await getDoc(userRef);
        if (!snapshot.exists()) return;
        const { uploadStatus, patientId } = snapshot.data();
        if (uploadStatus === 'uploading' && patientId) {
            await cleanupFailedUpload({ uid, patientId });
        }
    } catch (error) {
        console.error('Error checking for stale upload:', error);
    }
}

/**
 * Marks a patient document as failed and resets the user's upload status.
 * Called on any unrecoverable error after the patient document has been created.
 * Best-effort — never throws so it cannot disrupt the error handling path.
 * @param {string} uid
 * @param {string} patientId
 */
export async function cleanupFailedUpload({ uid, patientId }) {
    try {
        const patientRef = doc(getFirestore(operationsDbName), "patients", patientId);
        const userRef = doc(getFirestore(usersDbName), "users", uid);
        await Promise.all([
            updateDoc(patientRef, { step: "upload_failed" }),
            updateDoc(userRef, { uploadStatus: "done", patientId: null }),
        ]);
    } catch (cleanupError) {
        console.error('Error cleaning up failed upload:', cleanupError);
    }
}

/**
 * Finalizes all Firestore + API state once every file has been uploaded.
 * Handles both demo and regular user flows.
 *
 * @param {object} params
 * @param {string} params.uid
 * @param {string} params.patientId
 * @param {boolean} params.isDemo
 */
export async function finalizeUpload({ uid, patientId, isDemo }) {
    try {
        await patientImagesUploaded(uid, patientId);

        const userRef = doc(getFirestore(usersDbName), "users", uid);
        await updateDoc(userRef, { uploadStatus: "done" });

        const patientRef = doc(
            getFirestore(operationsDbName),
            "patients",
            patientId
        );
        // await updateDoc(patientRef, { step: "preprocess" });
    } catch (error) {
        console.error('Error finalizing upload:', error);
        throw new Error(`Failed to finalize upload: ${error.message}`);
    }
}

/**
 * @param {{
 *   uid: string,
 *   patientId: string,
 *   isDemo: boolean,
 *   onProgress: (percentage: number) => void,
 *   onComplete: () => void,
 * }} params
 */
export async function finalizeCompletedUpload({ uid, patientId, isDemo, onProgress, onComplete }) {
    try {
        onProgress(FINALIZING_PROGRESS);
        await finalizeUpload({ uid, patientId, isDemo });
        onProgress(COMPLETE_PROGRESS);
        onComplete();
    } catch (error) {
        console.error('Error in finalizeCompletedUpload:', error);
        throw error;
    }
}

// ---------------------------------------------------------------------------
// Upload orchestration
// ---------------------------------------------------------------------------

/**
 * Prepares upload metadata, creates Firestore records, and fans out all files
 * through DICOM de-identification before uploading to Firebase Storage.
 *
 * @param {{
 *   auth: any,
 *   storage: any,
 *   files: FileList,
 *   displayName: string,
 *   sex: string,
 *   tumor: boolean,
 *   examDate?: string | null,
 *   callbacks: {
 *     onProgress: (percentage: number) => void,
 *     onComplete: (isEmpty?: boolean) => void,
 *     onError?: (error: Error) => void,
 *   }
 * }} params
 */
export async function startUpload({ auth, storage, files, displayName, sex, tumor, examDate, callbacks }) {
    const { onProgress, onComplete, onError } = callbacks;
    
    // Input validation
    if (!auth?.currentUser?.uid) {
        const error = new Error('User not authenticated');
        if (onError) onError(error);
        throw error;
    }
    
    if (!displayName?.trim()) {
        const error = new Error('Display name is required');
        if (onError) onError(error);
        throw error;
    }
    
    if (typeof onProgress !== 'function' || typeof onComplete !== 'function') {
        throw new Error('Progress and complete callbacks are required');
    }
    
    const uid = auth.currentUser.uid;

    let patientId;
    let patientDocCreated = false;

    try {
        const userInfo = await fetchUserInfo(uid);
        const isDemo = Boolean(userInfo?.demo);
        const hospitalId = userInfo?.hospitalId;
        patientId = isDemo ? "demo-patient" : uuidv4();

        await createPatientDoc(patientId, uid, displayName, hospitalId, sex, tumor, examDate);
        patientDocCreated = true;
        await markUserUploading(uid, patientId);

        /** @type {ActiveUploadSession} */
        const session = { patientId, tasks: new Set(), cancelled: false, finalizing: false };
        activeUploadSessions.set(uid, session);

        /**
         * Synchronously claims the right to finalize this upload. Racing against
         * `cancelActiveUpload`'s own synchronous claim of `session.cancelled` — since
         * neither check-and-set crosses an `await`, exactly one of the two wins:
         * whichever runs first prevents the other from proceeding.
         * @returns {boolean} true if finalize may proceed
         */
        const claimFinalize = () => {
            if (session.cancelled || session.finalizing) return false;
            session.finalizing = true;
            return true;
        };

        const fileList = Array.from(files);
        const total = fileList.length;
        
        if (total === 0) {
            onComplete(true);
            return;
        }
        
        let uploaded = 0;
        let failed = 0;
        const failedFiles = [];
        
        // Deep clone DICOM fields to avoid mutation issues
        const sessionFields = DICOM_FIELDS.map(field => 
            Array.isArray(field) ? [...field] : field
        );
        
        const fileEntries = fileList.map((file) => ({
            file,
            path: `${uid}/${patientId}/${file.webkitRelativePath}`,
            size: file.size || 1,
        }));
        
        const totalBytes = fileEntries.reduce((sum, entry) => sum + entry.size, 0);
        const uploadedBytesByPath = new Map();

        const emitUploadProgress = () => {
            const pct = getAggregatedUploadProgress({ 
                uploadedBytesByPath, 
                totalBytes, 
                uploaded, 
                total 
            });
            onProgress(pct);
        };

        /** @param {string} path @param {number} size */
        const handleFileComplete = async (path, size) => {
            if (session.cancelled) return;
            uploadedBytesByPath.set(path, size);
            uploaded += 1;
            emitUploadProgress();

            if (uploaded + failed === total && claimFinalize()) {
                if (failed > 0) {
                    // Some files failed, but don't block completion
                    // Log the failures for monitoring
                    console.warn(`Upload completed with ${failed} failed files:`, failedFiles);
                    try {
                        await finalizeCompletedUpload({ uid, patientId, isDemo, onProgress, onComplete });
                    } catch (error) {
                        console.error('Error during finalization after partial upload:', error);
                        await cleanupFailedUpload({ uid, patientId });
                        if (onError) onError(error);
                        onComplete();
                    }
                } else {
                    // All files succeeded
                    try {
                        await finalizeCompletedUpload({ uid, patientId, isDemo, onProgress, onComplete });
                    } catch (error) {
                        console.error('Error during finalization:', error);
                        await cleanupFailedUpload({ uid, patientId });
                        if (onError) onError(error);
                        onComplete();
                    }
                }
            }
        };

        /** @param {string} path @param {number} size @param {unknown} err */
        const handleFileError = async (path, size, err) => {
            if (session.cancelled) return;
            console.error(`Upload failed for ${path}:`, err);
            failed += 1;
            failedFiles.push({ path, error: err });
            // Don't set uploadedBytesByPath for failed files
            emitUploadProgress();

            if (uploaded + failed === total && claimFinalize()) {
                // All uploads attempted (some succeeded, some failed)
                console.warn(`Upload completed with ${failed} failed files out of ${total}`);
                try {
                    await finalizeCompletedUpload({ uid, patientId, isDemo, onProgress, onComplete });
                } catch (error) {
                    console.error('Error during finalization after failed uploads:', error);
                    await cleanupFailedUpload({ uid, patientId });
                    if (onError) onError(error);
                    onComplete();
                }
            }
        };

        // Process uploads with concurrency control
        const processQueue = async () => {
            const activeUploads = new Set();
            
            for (const entry of fileEntries) {
                if (session.cancelled) break;

                // Wait if we've hit the concurrency limit
                while (activeUploads.size >= MAX_CONCURRENT_UPLOADS) {
                    await Promise.race(Array.from(activeUploads));
                }
                if (session.cancelled) break;

                const storageRef = ref(storage, entry.path);

                // Create a promise for this upload
                const uploadPromise = new Promise((resolve) => {
                    processAndUploadFile(entry.file, storageRef, sessionFields, {
                        /** @param {{ bytesTransferred: number }} snapshot */
                        onProgress: (snapshot) => {
                            if (session.cancelled) return;
                            uploadedBytesByPath.set(entry.path, Math.min(snapshot.bytesTransferred, entry.size));
                            emitUploadProgress();
                        },
                        onComplete: async () => {
                            await handleFileComplete(entry.path, entry.size);
                            activeUploads.delete(uploadPromise);
                            resolve();
                        },
                        /** @param {unknown} err */
                        onError: async (err) => {
                            await handleFileError(entry.path, entry.size, err);
                            activeUploads.delete(uploadPromise);
                            resolve();
                        },
                        shouldAbort: () => session.cancelled,
                        /** @param {any} uploadTask */
                        onTaskCreated: (uploadTask) => {
                            if (session.cancelled) {
                                uploadTask.cancel();
                                return;
                            }
                            session.tasks.add(uploadTask);
                        },
                    });
                });

                activeUploads.add(uploadPromise);
            }
            
            // Wait for all remaining uploads to complete
            await Promise.all(Array.from(activeUploads));
        };

        await processQueue();
        
    } catch (error) {
        console.error('Error in startUpload:', error);
        if (patientDocCreated) {
            await cleanupFailedUpload({ uid, patientId });
        }
        if (onError) onError(error);
        throw error;
    } finally {
        activeUploadSessions.delete(uid);
    }
}

/**
 * Cancels the active upload for a user, if any: stops any further files from
 * starting, cancels in-flight Firebase Storage upload tasks, and marks the
 * patient document as failed so it doesn't linger stuck at step "upload".
 * Safe to call when no upload is active for the uid (no-op).
 * @param {string} [uid]
 * @returns {Promise<void>}
 */
export async function cancelActiveUpload(uid) {
    if (!uid) return;
    const session = activeUploadSessions.get(uid);
    if (!session) return;
    if (session.finalizing) return;

    session.cancelled = true;
    session.tasks.forEach((task) => {
        try {
            task.cancel();
        } catch (err) {
            console.warn('Failed to cancel upload task:', err);
        }
    });

    await cleanupFailedUpload({ uid, patientId: session.patientId });
    activeUploadSessions.delete(uid);
}